/**
 * The Sunderfen map format. This is the single source of truth for world layout:
 * the server and client both load it, and every authoring tool (the generator,
 * the Tiled importer, a future in-game editor) writes it.
 *
 * Layout is a grid of square tiles. Tile (x, z) has its top-left corner at world (x, 0, z);
 * x runs east, z runs south. Heights are stored per tile in integer levels of `heightUnit`;
 * renderers derive vertex heights by averaging the tiles that touch each vertex.
 */
import { bytesToB64, b64ToBytes } from './character.js';

export const MAP_FORMAT = 'sunderfen-map';
export const MAP_VERSION = 1;

export const FLAG_BLOCK = 1;
export const FLAG_WATER = 2;
export const FLAG_BRIDGE = 4;

/** `prop` is scenery that blocks movement: market stalls and the like. */
/** 'decor': a placed thing you can walk through (a gate, an awning, a washing line). */
export type ObjectType = 'resource' | 'station' | 'landmark' | 'obstacle' | 'house' | 'bridge' | 'spawn' | 'npc' | 'creature' | 'prop' | 'camp' | 'decor' | 'patch';
export const OBJECT_TYPES: ObjectType[] = ['resource', 'station', 'landmark', 'obstacle', 'house', 'bridge', 'spawn', 'npc', 'creature', 'prop', 'camp', 'decor', 'patch'];

export interface MapObject {
  /** Stable id. Saves and network messages refer to objects by this. Never reuse a deleted id. */
  id: number;
  type: ObjectType;
  /** Resource kind, station kind, obstacle kind, landmark shape, etc. */
  kind: string;
  x: number;
  z: number;
  w?: number;
  h?: number;
  props?: Record<string, string | number | boolean>;
}

/**
 * How dangerous a region is. The world is laid out so danger rises with distance from
 * Meadowfold: home (nothing attacks first), middle, outer, frontier, and the reserved Scorch.
 */
export type Band = 'home' | 'middle' | 'outer' | 'frontier' | 'reserved';
export const BANDS: Record<Band, { name: string; order: number }> = {
  home: { name: 'Home', order: 0 }, middle: { name: 'Middle band', order: 1 }, outer: { name: 'Outer band', order: 2 }, frontier: { name: 'Frontier', order: 3 }, reserved: { name: 'The Scorch', order: 4 },
};
export const isBand = (v: unknown): v is Band => typeof v === 'string' && v in BANDS;

export interface RegionDef {
  id: number;
  key: string;
  name: string;
  /** Exploration xp for discovering it. */
  xp: number;
  /** Grass tint, 0..1 RGB. */
  grass: [number, number, number];
  /** Where the map label sits, in tiles. */
  label: [number, number];
  /** Danger band (missing: treated as home). */
  band?: Band;
  /** Combat level we advise before going in (0 or missing: none). */
  advise?: number;
  /** Who holds it, shown on entering (e.g. "Reaver territory"). */
  held?: string;
}

export interface TerrainDef { id: number; key: string; name: string; color: [number, number, number] }

/** A named place inside a region (a farm, a clearing, a harbour): shown when you walk in, and on the map. */
export interface PlaceDef { name: string; x: number; z: number; r: number; blurb?: string }

export interface MapFile {
  format: typeof MAP_FORMAT;
  version: number;
  name: string;
  width: number;
  height: number;
  heightUnit: number;
  /** Which generation of the world this is. Characters saved on an older one start fresh on it. */
  epoch?: number;
  terrains: TerrainDef[];
  regions: RegionDef[];
  /** Named places, laid out by hand (see the town layer). */
  places?: PlaceDef[];
  layers: {
    /** Int8 height levels, base64. */
    height: string;
    /** Uint8 terrain id, base64. */
    terrain: string;
    /** Uint8 region id, base64. */
    region: string;
    /** Uint8 FLAG_* bits, base64. */
    flags: string;
  };
  objects: MapObject[];
}

export const TERRAIN = { grass: 0, sand: 1, rock: 2, path: 3, seabed: 4, snow: 5, mud: 6 } as const;
export const DEFAULT_TERRAINS: TerrainDef[] = [
  { id: 0, key: 'grass', name: 'Grass (tinted by region)', color: [0.5, 0.66, 0.32] },
  { id: 1, key: 'sand', name: 'Sand', color: [0.85, 0.78, 0.57] },
  { id: 2, key: 'rock', name: 'Rock', color: [0.5, 0.49, 0.47] },
  { id: 3, key: 'path', name: 'Dirt path', color: [0.66, 0.56, 0.39] },
  { id: 4, key: 'seabed', name: 'Seabed', color: [0.55, 0.55, 0.45] },
  { id: 5, key: 'snow', name: 'Snow cap', color: [0.86, 0.86, 0.84] },
  { id: 6, key: 'mud', name: 'Mud', color: [0.38, 0.42, 0.28] },
];

export const encodeLayer = (a: Uint8Array | Int8Array) => bytesToB64(new Uint8Array(a.buffer, a.byteOffset, a.byteLength));
export const decodeU8 = (s: string) => b64ToBytes(s);
export const decodeI8 = (s: string) => { const b = b64ToBytes(s); return new Int8Array(b.buffer, b.byteOffset, b.byteLength); };

/** Height (world units) below which a tile counts as water. */
export const WATER_THRESHOLD = 0.14;
/** Height difference to a land neighbour that makes a tile too steep to stand on. */
export const STEEP_LIMIT = 1.25;

/**
 * Work out WATER and BLOCK flags from heights plus a hand-painted collision layer.
 * Shared by the generator and the Tiled importer so both treat terrain the same way.
 */
export function deriveFlags(width: number, height: number, heights: Int8Array, unit: number, collision?: Uint8Array): Uint8Array {
  const flags = new Uint8Array(width * height);
  const h = (x: number, z: number) => heights[x + z * width] * unit;
  for (let z = 0; z < height; z++) for (let x = 0; x < width; x++) {
    const t = x + z * width, me = h(x, z);
    let f = 0;
    if (me < WATER_THRESHOLD) f |= FLAG_WATER;
    if (collision && collision[t]) f |= FLAG_BLOCK;
    if (x < 2 || z < 2 || x >= width - 2 || z >= height - 2) f |= FLAG_BLOCK;
    if (!(f & FLAG_WATER)) {
      for (let dz = -1; dz <= 1 && !(f & FLAG_BLOCK); dz++) for (let dx = -1; dx <= 1; dx++) {
        const X = x + dx, Z = z + dz;
        if ((!dx && !dz) || X < 0 || Z < 0 || X >= width || Z >= height) continue;
        const n = h(X, Z);
        if (n < WATER_THRESHOLD) continue;
        if (Math.abs(n - me) > STEEP_LIMIT) { f |= FLAG_BLOCK; break; }
      }
    }
    flags[t] = f;
  }
  return flags;
}

/** Object types that stop movement through their tiles. Obstacles are special-cased per character. */
const BLOCKING: Partial<Record<ObjectType, true>> = { resource: true, station: true, landmark: true, house: true, prop: true, patch: true };

export class GameMap {
  readonly name: string;
  readonly width: number;
  readonly height: number;
  readonly heightUnit: number;
  readonly epoch: number;
  readonly heights: Int8Array;
  readonly terrain: Uint8Array;
  readonly region: Uint8Array;
  readonly flags: Uint8Array;
  readonly regions: RegionDef[];
  readonly places: PlaceDef[];
  readonly terrains: TerrainDef[];
  readonly objects: MapObject[];
  readonly objById = new Map<number, MapObject>();
  /** Object id on each tile, or -1. */
  readonly objAt: Int32Array;
  /** Obstacle id on each tile, or -1. */
  readonly obstacleAt: Int32Array;
  /** Walkable for everyone, regardless of unlocks. */
  readonly staticWalk: Uint8Array;
  /** Where returning characters (and anyone after the tutorial) arrive: Meadowfold. */
  readonly spawn: [number, number];
  /** Where brand-new characters start, if the map has a tutorial island. */
  readonly tutorialSpawn: [number, number] | null;
  private vh: Float32Array;

  constructor(file: MapFile) {
    if (file.format !== MAP_FORMAT) throw new Error('Not a Sunderfen map');
    if (file.version > MAP_VERSION) throw new Error(`Map version ${file.version} is newer than this build supports`);
    this.name = file.name; this.width = file.width; this.height = file.height; this.heightUnit = file.heightUnit; this.epoch = file.epoch ?? 1;
    this.heights = decodeI8(file.layers.height);
    this.terrain = decodeU8(file.layers.terrain);
    this.region = decodeU8(file.layers.region);
    this.flags = decodeU8(file.layers.flags);
    this.regions = file.regions; this.places = file.places ?? []; this.terrains = file.terrains; this.objects = file.objects;
    const n = this.width * this.height;
    if (this.heights.length !== n || this.terrain.length !== n || this.region.length !== n || this.flags.length !== n) throw new Error('Map layer size mismatch');
    this.objAt = new Int32Array(n).fill(-1);
    this.obstacleAt = new Int32Array(n).fill(-1);
    let spawn: [number, number] | null = null, tutorial: [number, number] | null = null;
    for (const o of this.objects) {
      if (this.objById.has(o.id)) throw new Error(`Duplicate object id ${o.id}`);
      this.objById.set(o.id, o);
      if (o.type === 'spawn') { if (o.kind === 'tutorial') tutorial = [o.x, o.z]; else spawn = [o.x, o.z]; continue; }
      if (o.type === 'npc' || o.type === 'creature' || o.type === 'camp') continue; // actors; their object is just a home position
      for (const t of this.objTiles(o)) {
        if (o.type === 'bridge') { this.flags[t] |= FLAG_BRIDGE; continue; }
        if (o.type === 'obstacle') { this.obstacleAt[t] = o.id; continue; }
        this.objAt[t] = o.id;
      }
    }
    this.spawn = spawn ?? [Math.floor(this.width / 2), Math.floor(this.height / 2)];
    this.tutorialSpawn = tutorial;
    this.staticWalk = new Uint8Array(n);
    for (let t = 0; t < n; t++) {
      const f = this.flags[t];
      const ground = (f & FLAG_BRIDGE) || !(f & (FLAG_BLOCK | FLAG_WATER));
      const o = this.objAt[t];
      this.staticWalk[t] = ground && this.obstacleAt[t] < 0 && !(o >= 0 && BLOCKING[this.objById.get(o)!.type]) ? 1 : 0;
    }
    // vertex heights: average of the tiles that touch each vertex
    const W = this.width + 1, Hh = this.height + 1;
    this.vh = new Float32Array(W * Hh);
    for (let z = 0; z < Hh; z++) for (let x = 0; x < W; x++) {
      let s = 0, c = 0;
      for (let dz = -1; dz <= 0; dz++) for (let dx = -1; dx <= 0; dx++) {
        const X = x + dx, Z = z + dz;
        if (X < 0 || Z < 0 || X >= this.width || Z >= this.height) continue;
        s += this.heights[X + Z * this.width]; c++;
      }
      this.vh[x + z * W] = (s / c) * this.heightUnit;
    }
  }

  idx(x: number, z: number) { return x + z * this.width; }
  tx(t: number) { return t % this.width; }
  tz(t: number) { return (t / this.width) | 0; }
  inBounds(x: number, z: number) { return x >= 0 && z >= 0 && x < this.width && z < this.height; }

  /** Move a 1×1 non-blocking object (a fishing spot) to another tile. */
  moveObject(o: MapObject, x: number, z: number) {
    const from = this.idx(o.x, o.z);
    if (this.objAt[from] === o.id) this.objAt[from] = -1;
    o.x = x; o.z = z;
    this.objAt[this.idx(x, z)] = o.id;
  }

  objTiles(o: MapObject): number[] {
    const out: number[] = [];
    for (let z = o.z; z < o.z + (o.h ?? 1); z++) for (let x = o.x; x < o.x + (o.w ?? 1); x++) if (this.inBounds(x, z)) out.push(this.idx(x, z));
    return out;
  }

  /** Is tile t walkable for a character who has cleared these obstacle ids? */
  walkable(t: number, cleared?: ReadonlySet<number>): boolean {
    if (this.staticWalk[t]) return true;
    const ob = this.obstacleAt[t];
    return ob >= 0 && !!cleared && cleared.has(ob);
  }

  vertexHeight(vx: number, vz: number) { return this.vh[vx + vz * (this.width + 1)]; }

  /** Terrain surface height at a world position (matches the rendered triangles). */
  heightAt(x: number, z: number): number {
    x = Math.min(Math.max(x, 0), this.width - 0.0001); z = Math.min(Math.max(z, 0), this.height - 0.0001);
    const xi = x | 0, zi = z | 0, fx = x - xi, fz = z - zi;
    const a = this.vertexHeight(xi, zi), b = this.vertexHeight(xi + 1, zi), c = this.vertexHeight(xi, zi + 1), d = this.vertexHeight(xi + 1, zi + 1);
    // The renderer alternates the diagonal per tile (checkerboard); match it exactly.
    if ((xi + zi) & 1) {
      if (fx + fz <= 1) return a + (b - a) * fx + (c - a) * fz;
      return d + (c - d) * (1 - fx) + (b - d) * (1 - fz);
    }
    if (fx >= fz) return a + (b - a) * (fx - fz) + (d - a) * fz;
    return a + (c - a) * (fz - fx) + (d - a) * fx;
  }

  /** Where feet go: bridge decks and stepping stones float above water. */
  surfaceY(x: number, z: number, cleared?: ReadonlySet<number>): number {
    const tx = Math.floor(x), tz = Math.floor(z);
    if (this.inBounds(tx, tz)) {
      const t = this.idx(tx, tz);
      if (this.flags[t] & FLAG_BRIDGE) return 0.5;
      const ob = this.obstacleAt[t];
      if (ob >= 0 && cleared?.has(ob) && (this.flags[t] & FLAG_WATER)) return this.objById.get(ob)!.kind === 'stones' ? 0.36 : 0.5;
    }
    return Math.max(this.heightAt(x, z), -0.3);
  }

  /** The named place at (x, z), if any: the nearest whose circle you're inside. */
  placeAt(x: number, z: number): PlaceDef | undefined {
    let best: PlaceDef | undefined, bd = Infinity;
    for (const p of this.places) { const d = Math.hypot(p.x - x, p.z - z); if (d <= p.r && d / p.r < bd) { bd = d / p.r; best = p; } }
    return best;
  }

  regionAt(x: number, z: number): RegionDef | undefined {
    if (!this.inBounds(x, z)) return undefined;
    return this.regions.find(r => r.id === this.region[this.idx(x, z)]);
  }
}
